/**
 * Meta Integration OAuth Utilities (Story 9.1, Task 1)
 *
 * Handles Meta OAuth URL building, scope parsing, error parsing,
 * and scope reduction detection for all 4 Meta platforms.
 *
 * Key differences from Google OAuth:
 * - No PKCE — Meta uses client_secret + session binding
 * - Comma-separated scopes (not space-separated)
 * - No refresh_token — long-lived token IS the refresh mechanism
 * - Selective permission deselection on consent screen
 */

import {
  META_INTEGRATION_SCOPES,
  ALL_META_SCOPES,
  META_GRAPH_API_VERSION,
  META_PLATFORM_VALUES,
  type MetaPlatform,
} from "./scopes";

// ============================================================================
// Constants
// ============================================================================

const META_AUTH_URL = `https://www.facebook.com/${META_GRAPH_API_VERSION}/dialog/oauth`;

// ============================================================================
// Types
// ============================================================================

export interface MetaIntegrationAuthUrlOptions {
  appId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** Use auth_type=rerequest for incremental consent */
  isRerequest?: boolean;
  /**
   * "Add another grant" flow (Story 32.2): when true, `auth_type` is set to
   * `reauthenticate` — Meta's documented force-credential-re-entry mechanism,
   * the closest thing it offers to an account chooser. `auth_type` is
   * SINGLE-VALUED: forceNewGrant WINS over isRerequest (never both).
   */
  forceNewGrant?: boolean;
}

export interface MetaCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "contact-admin" | "add-scopes" | "none" | "upgrade";
}

export interface ScopeReductionResult {
  removed: string[];
  affectedPlatforms: MetaPlatform[];
}

// ============================================================================
// Build Authorization URL (Task 1.1)
// ============================================================================

/**
 * Builds the Meta OAuth authorization URL.
 *
 * Key differences from Google:
 * - No PKCE (code_verifier/challenge) — Meta uses client_secret
 * - Scopes are comma-separated (not space-separated)
 * - auth_type=rerequest for incremental consent
 * - All Meta scopes requested in a single consent flow
 */
export function buildMetaIntegrationAuthUrl(
  options: MetaIntegrationAuthUrlOptions
): string {
  const { appId, redirectUri, stateKey, isRerequest, forceNewGrant } = options;

  if (!appId) {
    throw new Error("META_APP_ID is required");
  }
  if (!redirectUri) {
    throw new Error("META_OAUTH_REDIRECT_URI is required");
  }

  const url = new URL(META_AUTH_URL);
  url.searchParams.set("client_id", appId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("response_type", "code");
  // Meta uses comma-separated scopes
  url.searchParams.set("scope", ALL_META_SCOPES.join(","));

  // auth_type is single-valued (rerequest | reauthenticate | reauthorize) —
  // the Story 32.2 forced flow wins over incremental-consent rerequest.
  if (forceNewGrant) {
    url.searchParams.set("auth_type", "reauthenticate");
  } else if (isRerequest) {
    url.searchParams.set("auth_type", "rerequest");
  }

  return url.toString();
}

// ============================================================================
// Scope Parsing (Task 1.3, 1.4)
// ============================================================================

/**
 * Parses granted scopes from Meta's token response.
 *
 * CRITICAL: Meta uses COMMA-separated scopes (not space-separated like Google).
 * The scope field may also be absent on error responses.
 */
export function parseMetaGrantedScopes(scopeString: string): string[] {
  if (!scopeString) return [];
  return scopeString
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Determines which Meta platforms are authorized based on granted scopes.
 *
 * Returns platform keys (e.g., "facebook-pages") for platforms
 * where ALL required scopes have been granted.
 */
export function getEnabledMetaPlatforms(grantedScopes: string[]): MetaPlatform[] {
  const grantedSet = new Set(grantedScopes);
  const enabled: MetaPlatform[] = [];

  for (const [platform, config] of Object.entries(META_INTEGRATION_SCOPES)) {
    const allScopesGranted = config.scopes.every((s) => grantedSet.has(s));
    if (allScopesGranted) {
      enabled.push(platform as MetaPlatform);
    }
  }

  return enabled;
}

// ============================================================================
// Error Parsing (Task 1.5)
// ============================================================================

/**
 * Parse Meta OAuth callback errors from query params.
 *
 * Meta returns errors as query params on redirect:
 * ?error=access_denied&error_reason=user_denied&error_code=200&error_description=...
 *
 * Maps to user-friendly messages per the Meta Error Catalog in Dev Notes.
 */
export function parseMetaCallbackError(
  error: string,
  errorReason?: string,
  errorCode?: string,
  errorDescription?: string
): MetaCallbackError {
  // User cancelled
  if (error === "access_denied" && errorReason === "user_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You cancelled the Meta connection.",
      userAction: "retry",
    };
  }

  // Access denied (other reasons)
  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "Meta denied the connection request.",
      userAction: "retry",
    };
  }

  // App not approved (error code 100)
  if (errorCode === "100") {
    return {
      code: "oauth-app-not-approved",
      userMessage:
        "Meta hasn't approved this connection yet. Please try later.",
      userAction: "none",
    };
  }

  // Rate limited (error codes 4, 32)
  if (errorCode === "4" || errorCode === "32") {
    return {
      code: "oauth-rate-limited",
      userMessage:
        "Meta is rate limiting requests. Please try again shortly.",
      userAction: "retry",
    };
  }

  // Redirect URI mismatch
  if (error === "invalid_request" && errorDescription?.includes("redirect_uri")) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Meta connection is misconfigured. Contact support.",
      userAction: "contact-admin",
    };
  }

  // Default / unknown error
  return {
    code: "oauth-error",
    userMessage:
      errorDescription ||
      "An error occurred connecting to Meta. Please try again.",
    userAction: "retry",
  };
}

// ============================================================================
// Scope Reduction Detection (Task 1.7)
// ============================================================================

/**
 * Detects scope reduction when a user re-authorizes Meta and
 * deselects previously granted permissions.
 *
 * Returns removed scopes and which platforms are affected.
 */
export function detectScopeReduction(
  previousScopes: string[],
  newScopes: string[]
): ScopeReductionResult {
  const newSet = new Set(newScopes);
  const removed = previousScopes.filter((s) => !newSet.has(s));

  if (removed.length === 0) {
    return { removed: [], affectedPlatforms: [] };
  }

  // Determine which platforms are affected by the removed scopes
  const removedSet = new Set(removed);
  const affectedPlatforms: MetaPlatform[] = [];

  for (const platform of META_PLATFORM_VALUES) {
    const config = META_INTEGRATION_SCOPES[platform];
    if (config) {
      const hasRemovedScope = config.scopes.some((s) => removedSet.has(s));
      if (hasRemovedScope) {
        affectedPlatforms.push(platform);
      }
    }
  }

  return { removed, affectedPlatforms };
}

// Re-exports
export {
  META_INTEGRATION_SCOPES,
  ALL_META_SCOPES,
  META_GRAPH_API_VERSION,
  META_OAUTH_PLATFORMS,
  META_PLATFORM_VALUES,
  type MetaScopeConfig,
  type MetaPlatform,
} from "./scopes";
